import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription, catchError, combineLatest, map, of, startWith, switchMap } from 'rxjs';
import { isJoinRequest } from '../../../models/church-affiliation-invite.model';
import { Group, GroupType } from '../../../models/group.model';
import { MemberType } from '../../../models/membership.status';
import { Organization } from '../../../models/organization.model';
import { ManagementService } from '../../../services/management.service';
import { AppState } from '../../../state/appstate';
import * as groupSelector from '../../../state/selectors/group.selector';

export type ChurchJoinStatus = 'member' | 'otherOrg' | 'pending' | 'canRequest';

export interface ChurchJoinOption {
  church: Group;
  status: ChurchJoinStatus;
}

/**
 * Landing page of an organization's join link / QR code (shared from Add church in the
 * org chart). A church admin picks one of their churches and requests to join; the
 * organization then approves the request in its org chart.
 */
@Component({
  selector: 'app-join-organization',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './join-organization.component.html',
})
export class JoinOrganizationComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private store = inject(Store<AppState>);
  private managementService = inject(ManagementService);
  private toastr = inject(ToastrService);
  private logger = inject(NGXLogger);

  private subs = new Subscription();

  orgId = '';
  // undefined while loading, null when the organization doesn't exist
  organization$: Observable<Organization | null | undefined> = of(undefined);
  options$: Observable<ChurchJoinOption[]> = of([]);

  // Church the request form is open for, its message, and the church being submitted
  openChurchId: string | null = null;
  joinMessage = '';
  submittingChurchId: string | null = null;
  // Requests sent from this page, shown as pending before the listener catches up
  private sentTo = new Set<string>();

  ngOnInit(): void {
    this.subs.add(
      this.route.params.subscribe((params) => {
        this.orgId = params['orgId'] || '';
        this.logger.debug('join-organization.component.ts :: Opened join link:', { orgId: this.orgId });
        this.organization$ = this.managementService.getOrganization(this.orgId).pipe(
          startWith(undefined),
          catchError((err) => {
            this.logger.error('join-organization.component.ts :: Loading organization failed:', { orgId: this.orgId, err });
            return of(null);
          })
        );
        this.options$ = this.buildOptions(this.orgId);
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /** Churches the signed-in user administers, with where each stands with this organization */
  private buildOptions(orgId: string): Observable<ChurchJoinOption[]> {
    return this.store.select(groupSelector.getAllGroups).pipe(
      map((groups) =>
        groups.filter(
          (g) =>
            g.groupType === GroupType.church &&
            (g.memberType as MemberType[] | undefined)?.includes(MemberType.admin)
        )
      ),
      switchMap((churches) => {
        if (churches.length === 0) return of([]);
        return combineLatest(
          churches.map((church) =>
            this.managementService.getChurchAffiliationInvites(church.id).pipe(
              map((invites) => {
                const hasPendingRequest = invites.some(
                  (i) => isJoinRequest(i) && i.organizationId === orgId
                );
                return { church, status: this.statusFor(church, orgId, hasPendingRequest) };
              })
            )
          )
        );
      })
    );
  }

  private statusFor(church: Group, orgId: string, hasPendingRequest: boolean): ChurchJoinStatus {
    if (church.organizationId === orgId) return 'member';
    if (church.organizationId) return 'otherOrg';
    if (hasPendingRequest || this.sentTo.has(church.id)) return 'pending';
    return 'canRequest';
  }

  openRequest(church: Group): void {
    this.logger.debug('join-organization.component.ts :: Request to join clicked:', { orgId: this.orgId, churchId: church.id });
    this.openChurchId = church.id;
    this.joinMessage = '';
  }

  cancelRequest(): void {
    this.logger.debug('join-organization.component.ts :: Request cancelled:', { orgId: this.orgId, churchId: this.openChurchId });
    this.openChurchId = null;
    this.joinMessage = '';
  }

  async sendRequest(church: Group, organization: Organization): Promise<void> {
    this.logger.debug('join-organization.component.ts :: Send request clicked:', { orgId: this.orgId, churchId: church.id });
    this.submittingChurchId = church.id;
    try {
      await this.managementService.requestToJoinOrganization(this.orgId, church.id, this.joinMessage.trim() || undefined);
      this.logger.debug('join-organization.component.ts :: Join request sent:', { orgId: this.orgId, churchId: church.id });
      this.sentTo.add(church.id);
      this.options$ = this.buildOptions(this.orgId);
      this.openChurchId = null;
      this.joinMessage = '';
      this.toastr.success(`Request sent. ${organization.name} will review it.`);
    } catch (err: any) {
      this.logger.error('join-organization.component.ts :: Join request failed:', { orgId: this.orgId, churchId: church.id, err });
      this.toastr.error(err?.message || 'Could not send the request. Please try again.');
    } finally {
      this.submittingChurchId = null;
    }
  }
}
